import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@mui/material/styles";

const BaseDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerAdornment,
  actions,
  actionsSx,
  children,
  contentSx,
  maxWidth = "sm",
  closeDisabled = false,
  dialogSx,
  backdropSx,
  paperSx,
  iconColor = "primary.main",
}) => {
  const handleDialogClose = (_event, reason) => {
    if (closeDisabled) return;
    onClose?.(reason === "backdropClick" ? false : undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={closeDisabled}
      maxWidth={maxWidth}
      fullWidth
      sx={dialogSx}
      BackdropProps={{ sx: backdropSx }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.15)",
          overflow: "visible",
          ...paperSx,
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          {icon && (
            <Box
              sx={(theme) => {
                const paletteKey = String(iconColor).split(".")[0];
                const iconBaseColor =
                  theme.palette[paletteKey]?.main || theme.palette.primary.main;

                return {
                  p: 1,
                  bgcolor: alpha(iconBaseColor, 0.1),
                  color: iconColor,
                  borderRadius: 2,
                };
              }}
            >
              {icon}
            </Box>
          )}

          <Box>
            <Typography variant='h6' fontWeight={700}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant='caption' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ flexShrink: 0, ml: 2 }}
        >
          {headerAdornment}
          {!closeDisabled && (
            <IconButton
              onClick={() => !closeDisabled && onClose?.()}
              disabled={closeDisabled}
              size='small'
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ p: 3, ...contentSx }}>{children}</DialogContent>

      {/* ACTIONS */}
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: "background.default",
            borderTop: "1px solid",
            borderColor: "divider",
            borderRadius: 4,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            ...actionsSx,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseDialog;
